/**
 * EntranceExitLogs Use Case
 * 입출입 로그 관리 훅 (Polling 방식 실시간 업데이트 + 성능 최적화)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { entranceExitLogsRepository } from '../repositories/EntranceExitLogsRepository';
import { isRealtimeEnabled } from '../../../config/supabase';
import * as XLSX from 'xlsx';
import type {
  EntranceExitLog,
  EntranceExitStats,
  LogFilter,
  ActionFilter,
} from '../models/EntranceExitLogs';
import { getTodayKST, formatDateKST } from '../../../utils/dateUtils';

const ITEMS_PER_PAGE = 20;

type DateRangeType = 'today' | 'threeDays' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

// 기본 KST 날짜 계산 (폴백용)
const getKSTDateString = () => {
  const now = new Date();
  const kstTime = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return formatDateKST(kstTime);
};

export const useEntranceExitLogs = (enabled: boolean = true) => {
  const [logs, setLogs] = useState<EntranceExitLog[]>([]);
  const [stats, setStats] = useState<EntranceExitStats | null>(null);
  const [loading, setLoading] = useState(true); // 초기 로딩 true로 시작
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  // enabled가 false면 폴링 실행 안함

  // 논리적 오늘 날짜 (day_boundary_hour 적용)
  const [logicalToday, setLogicalToday] = useState<string>(''); // 빈 문자열로 시작 (로딩 중)
  const [dayBoundaryHour, setDayBoundaryHour] = useState<number>(4);
  const [isInitialized, setIsInitialized] = useState(false); // 초기화 완료 여부
  const logicalTodayFetched = useRef(false);

  const [filter, setFilter] = useState<LogFilter>({
    startDate: '',
    endDate: '',
    userName: '',
    action: 'all',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 기간 필터 상태
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('today');
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    startDate: '',
    endDate: '',
  });

  // 논리적 오늘 날짜 조회 (day_boundary_hour 적용) - 초기화 시 한 번만 실행
  useEffect(() => {
    if (logicalTodayFetched.current) return;
    logicalTodayFetched.current = true;

    const fetchLogicalToday = async () => {
      try {
        const result = await entranceExitLogsRepository.getLogicalToday();
        let todayDate: string;

        if (result?.success && result.data?.logical_today) {
          todayDate = result.data.logical_today;
          setDayBoundaryHour(result.data.day_boundary_hour);
        } else {
          // API 실패 시 폴백: 물리적 오늘 사용
          todayDate = getKSTDateString();
        }

        // 모든 상태를 한 번에 설정
        setLogicalToday(todayDate);
        // 초기: 날짜 필터 없이 최근 30건 조회
        setFilter({
          startDate: '',
          endDate: '',
          userName: '',
          action: 'all',
        });
        setCustomDateRange({
          startDate: todayDate,
          endDate: todayDate,
        });
        setIsInitialized(true); // 초기화 완료
      } catch (error) {
        console.error('논리적 오늘 조회 실패:', error);
        // 에러 시에도 폴백으로 초기화
        const fallbackDate = getKSTDateString();
        setLogicalToday(fallbackDate);
        // 초기: 날짜 필터 없이 최근 30건 조회
        setFilter({
          startDate: '',
          endDate: '',
          userName: '',
          action: 'all',
        });
        setCustomDateRange({
          startDate: fallbackDate,
          endDate: fallbackDate,
        });
        setIsInitialized(true);
      }
    };

    fetchLogicalToday();
  }, []);

  /**
   * 날짜 범위 계산 (논리적 오늘 기준, day_boundary_hour 적용)
   */
  const getDateRange = useCallback((): DateRange => {
    // 논리적 오늘 사용 (day_boundary_hour 적용됨)
    const todayStr = logicalToday;

    switch (dateRangeType) {
      case 'today':
        // 날짜 필터가 비어있으면 최근 30건 모드 (날짜 없이 조회)
        if (!filter.startDate && !filter.endDate) {
          return { startDate: '', endDate: '' };
        }
        return { startDate: todayStr, endDate: todayStr };
      case 'threeDays': {
        // 논리적 오늘 기준 3일 전
        const [year, month, day] = todayStr.split('-').map(Number);
        const logicalTodayDate = new Date(year, month - 1, day);
        const threeDaysAgo = new Date(logicalTodayDate.getTime() - 2 * 24 * 60 * 60 * 1000); // 오늘 포함 3일
        return {
          startDate: formatDateKST(threeDaysAgo),
          endDate: todayStr,
        };
      }
      case 'custom':
        return customDateRange;
      default:
        return { startDate: todayStr, endDate: todayStr };
    }
  }, [dateRangeType, customDateRange, logicalToday]);

  /**
   * 로그 조회 (페이지네이션 적용)
   */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();

      // 필터 객체에 날짜 범위 반영
      const currentFilter: LogFilter = {
        ...filter,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      const data = await entranceExitLogsRepository.getLogs({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        filter: currentFilter,
      });

      if (data?.success) {
        setLogs(data.data.items);
        setTotalLogs(data.data.pagination.total);
        setTotalPages(data.data.pagination.total_pages);

        // 로그 데이터로 간단한 통계 계산 (페이지네이션된 데이터 기준)
        // 주의: 전체 통계는 별도 API 호출 필요 (현재는 페이지별 통계만)
        const entries = data.data.items.filter(log => log.action === 'entry').length;
        const exits = data.data.items.filter(log => log.action === 'exit').length;
        const outings = data.data.items.filter(log => log.action === 'outing').length;

        setStats({
          date: dateRange.startDate,
          total_entries: entries,
          total_exits: exits,
          total_outings: outings,
          current_inside: Math.max(0, entries - exits - outings),
          current_inside_members: [],
          hourly_stats: {},
        });
      } else {
        setLogs([]);
        setTotalLogs(0);
        setTotalPages(0);
        setStats({
          date: dateRange.startDate,
          total_entries: 0,
          total_exits: 0,
          total_outings: 0,
          current_inside: 0,
          current_inside_members: [],
          hourly_stats: {},
        });
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter, getDateRange]);

  /**
   * 필터 변경 핸들러
   */
  const handleFilterChange = useCallback((newFilter: Partial<LogFilter>) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
    setCurrentPage(1); // 필터 변경 시 1페이지로 리셋
  }, []);

  /**
   * 액션 필터 변경
   */
  const handleActionFilterChange = useCallback((action: ActionFilter) => {
    handleFilterChange({ action });
  }, [handleFilterChange]);

  /**
   * 이름 검색 변경
   */
  const handleSearchChange = useCallback((userName: string) => {
    handleFilterChange({ userName });
  }, [handleFilterChange]);

  /**
   * 필터 초기화 (논리적 오늘 기준)
   */
  const handleResetFilters = useCallback(() => {
    setFilter({
      startDate: '',
      endDate: '',
      userName: '',
      action: 'all',
    });
    setDateRangeType('today');
    setCustomDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1);
  }, []);

  /**
   * 페이지 변경
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * 이전 페이지
   */
  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  /**
   * 다음 페이지
   */
  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  /**
   * 날짜 변경 (단일 날짜)
   */
  const handleDateChange = useCallback((date: string) => {
    handleFilterChange({ startDate: date, endDate: date });
  }, [handleFilterChange]);

  /**
   * 기간 필터 변경
   */
  const handleDateRangeChange = useCallback((type: DateRangeType) => {
    setDateRangeType(type);
    setCurrentPage(1);
  }, []);

  /**
   * 커스텀 날짜 범위 변경
   */
  const handleCustomDateRangeChange = useCallback((range: DateRange) => {
    setCustomDateRange(range);
    setDateRangeType('custom');
    setFilter(prev => ({
      ...prev,
      startDate: range.startDate,
      endDate: range.endDate,
    }));
    setCurrentPage(1);
  }, []);

  /**
   * 엑셀 다운로드 핸들러 (Truncate 경고 추가)
   */
  const handleExportToExcel = useCallback(async () => {
    try {
      const dateRange = getDateRange();

      // Export 필터 객체 생성
      const exportFilter: LogFilter = {
        ...filter,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      // Export API 호출
      const data = await entranceExitLogsRepository.exportLogs(exportFilter);

      if (!data?.success || !data.data.logs.length) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }

      // Truncate 경고 체크
      if (data.data.is_truncated) {
        const confirmed = window.confirm(
          `총 ${data.data.total.toLocaleString()}건 중 ` +
          `${data.data.exported.toLocaleString()}건만 다운로드됩니다.\n\n` +
          `기간을 더 좁혀서 다시 내려받는 것을 권장합니다.\n\n` +
          `그래도 진행하시겠습니까?`
        );
        if (!confirmed) return;
      }

      // 엑셀 데이터 변환
      const excelData = data.data.logs.map((log, index) => ({
        번호: index + 1,
        시간: new Date(log.timestamp).toLocaleString('ko-KR'),
        회원명: log.user_name,
        회원ID: log.user_id,
        동작: log.action === 'entry' ? '입장' : log.action === 'exit' ? '퇴장' : '외출',
        인식방법: log.method === 'face' ? '얼굴인식' : log.method === 'code' ? '번호인식' : log.method === 'phone' ? '전화번호' : log.method,
        게이트: `게이트 ${log.gate_id}`,
        신뢰도: log.confidence ? `${Math.round(log.confidence * 100)}%` : '-',
      }));

      // 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // 컬럼 너비 설정
      const wscols = [
        { wch: 5 },  // 번호
        { wch: 20 }, // 시간
        { wch: 12 }, // 회원명
        { wch: 38 }, // 회원ID
        { wch: 8 },  // 동작
        { wch: 12 }, // 인식방법
        { wch: 10 }, // 게이트
        { wch: 10 }, // 신뢰도
      ];
      worksheet['!cols'] = wscols;

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '입출입기록');

      // 파일명 생성
      const filename = `입출입기록_${dateRange.startDate}_${dateRange.endDate}.xlsx`;

      // 다운로드
      XLSX.writeFile(workbook, filename);

      // 성공 메시지 (다운로드 건수 표시)
      alert(`${data.data.exported.toLocaleString()}건의 입출입 기록을 다운로드했습니다.`);
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      alert('엑셀 다운로드에 실패했습니다.');
    }
  }, [filter, getDateRange]);

  // 로그 조회 (초기화 완료 후, 필터 변경 시 자동 호출)
  useEffect(() => {
    // 초기화가 완료되지 않았으면 조회하지 않음
    if (!isInitialized) return;
    fetchLogs();
  }, [isInitialized, fetchLogs]);

  // Polling 방식 실시간 업데이트 (5초 간격, 검색 중 일시정지)
  // 주의: Realtime 모드가 활성화된 경우 이 Polling은 실행되지 않음
  useEffect(() => {
    // 초기화가 완료되지 않았으면 Polling 시작하지 않음
    if (!isInitialized) return;

    // Realtime 모드가 활성화된 경우 Polling 하지 않음
    if (isRealtimeEnabled()) {
      setIsRealtimeConnected(false);
      return;
    }

    // enabled가 false면 폴링 실행 안함
    if (!enabled) {
      setIsRealtimeConnected(false);
      return;
    }

    setIsRealtimeConnected(true);

    // 오늘 날짜만 자동 새로고침
    if (dateRangeType !== 'today') {
      setIsRealtimeConnected(false);
      return;
    }

    // 검색어 입력 중이면 자동 새로고침 중지
    if (filter.userName && filter.userName.length > 0) {
      setIsRealtimeConnected(true); // 연결 상태는 유지
      return;
    }

    // 30초마다 자동 새로고침 (DB 부하 최소화)
    const intervalId = setInterval(() => {
      // 새로고침 시점에 다시 한번 검색 중인지 확인
      if (filter.userName && filter.userName.length > 0) {
        return;
      }

      fetchLogs();
    }, 30000);

    return () => {
      clearInterval(intervalId);
      setIsRealtimeConnected(false);
    };
  }, [enabled, isInitialized, dateRangeType, filter.userName, fetchLogs]);

  return {
    // 상태
    logs,
    stats,
    loading,
    isRealtimeConnected,
    currentPage,
    totalLogs,
    totalPages,
    itemsPerPage: ITEMS_PER_PAGE,

    // 필터 상태
    filter,
    dateRangeType,
    customDateRange,

    // 액션 (하위 호환성 유지)
    handleFilterChange,
    handleActionFilterChange,
    handleSearchChange,
    handleResetFilters,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    handleDateChange,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExportToExcel,
    getDateRange,

    // 추가 편의 속성 (기존 코드 호환)
    selectedDate: filter.startDate || getTodayKST(),
    searchName: filter.userName || '',
    actionFilter: filter.action || 'all',
  };
};
